import { LocalDate } from '@js-joda/core';
import { useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import { createTranslatedText, dateFormatter } from '@dreryk/react-common';

import { calculateNewIssuedToLocalDate } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../translations';
import {
   DraftPrescriptionFieldName,
   DraftPrescriptionsFormController,
} from '../../draftPrescriptions/DraftPrescriptionsForm.types';
import { AdditionalRequirement } from '../../prescription/additionalRequirements/additionalRequirements.types';
import { PrescriptionTextValue } from '../../prescription/PrescriptionTextValue';

type IssuedToTextValueProps = Pick<DraftPrescriptionsFormController, 'setValue'> & {
   readonly name: DraftPrescriptionFieldName;
   readonly defaultValue: LocalDate | null;
   readonly issuedFrom: LocalDate | null;
   readonly additionalRequirements: AdditionalRequirement[] | null;
};

export const IssuedToTextValue = (props: IssuedToTextValueProps): JSX.Element => {
   const { defaultValue, setValue, name, issuedFrom, additionalRequirements } = props;
   const [currentDate, setCurrentDate] = useState(defaultValue);

   const issuedForYear = useMemo<boolean>(
      () => Boolean(additionalRequirements?.some(({ type }) => type === 'FOR_YEAR')),
      [additionalRequirements]
   );

   useDeepCompareEffect(() => {
      const newIssuedToDate = calculateNewIssuedToLocalDate(issuedFrom, issuedForYear);
      setValue(name, newIssuedToDate);
      setCurrentDate(newIssuedToDate);
   }, [issuedFrom, issuedForYear]);

   const formattedValue = useMemo(() => createTranslatedText(dateFormatter(currentDate)), [currentDate]);

   return (
      <PrescriptionTextValue
         testId={'issuedTo'}
         label={prescriptionTranslationKeys.issueDate.issuedTo}
         value={formattedValue}
      />
   );
};
